import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConflictResolutionModal from '../modals/ConflictResolutionModal';
import apiClient from '../../utils/apiClient';
import './SmartDuplicateDetector.css';

/**
 * SmartDuplicateDetector - Détection automatique de nœuds en double
 * 
 * Utilise les embeddings vectoriels pour identifier automatiquement les nœuds
 * qui sont sémantiquement similaires (duplicatas potentiels).
 * 
 * Fonctionnalités :
 * - Détection automatique par similarité cosinus (threshold 0.85)
 * - Affichage visuel de la similarité et des raisons
 * - Fusion en 1 clic (préserve le nœud le plus ancien)
 * - Marquage de faux positifs pour apprentissage ML
 * - Comparaison détaillée côte à côte
 */
const SmartDuplicateDetector = () => {
  const { t } = useTranslation();
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [threshold, setThreshold] = useState(0.85);
  const [selectedPair, setSelectedPair] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [mergeConflict, setMergeConflict] = useState(null);
  const [isResolving, setIsResolving] = useState(false);

  // Charger les duplicatas au montage
  useEffect(() => {
    loadDuplicates();
  }, [threshold]);

  const loadDuplicates = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/api/smart-tools/detect-duplicates?threshold=${threshold}`);
      setDuplicates(data.duplicates || []);
      
      if (data.duplicates.length === 0) {
        toast.info(t('smartTools.duplicateDetector.noDuplicates'));
      } else {
        toast.success(t('smartTools.duplicateDetector.foundDuplicates', { count: data.duplicates.length }));
      }
    } catch (error) {
      console.error('Error loading duplicates:', error);
      toast.error(t('smartTools.duplicateDetector.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async (pair) => {
    // Ouvrir la modal de merge interactive au lieu du simple confirm
    setMergeConflict({
      conflictType: 'semantic_duplicate',
      existingNode: pair.nodeA, // Le nœud le plus ancien (conservé)
      newData: pair.nodeB, // Le nœud dupliqué (sera archivé)
      similarity: pair.similarity,
      recommendation: 'merge',
      detailedScores: pair.reasons || {},
      explanation: `Similarité sémantique de ${(pair.similarity * 100).toFixed(1)}% détectée entre ces deux nœuds.`
    });
  };

  const handleMergeResolve = async (resolution) => {
    setIsResolving(true);
    try {
      const { action, existingNode, mergedData } = resolution;

      if (action === 'create') {
        // L'utilisateur veut garder les deux nœuds séparés
        toast.info(t('smartTools.duplicateDetector.keptSeparate'));
        setMergeConflict(null);
        setIsResolving(false);
        return;
      }

      // Sinon, effectuer la fusion avec les données mergées
      const { data: result } = await apiClient.post('/api/smart-tools/merge-nodes', {
        nodeAId: existingNode.id,
        nodeBId: mergeConflict.newData.id,
        mergedData: mergedData // Données fusionnées personnalisées
      });
      toast.success(result.message || t('smartTools.duplicateDetector.mergeSuccess'));
      
      // Retirer la paire fusionnée de la liste
      setDuplicates(prev => prev.filter(d => 
        !(d.nodeA.id === existingNode.id && d.nodeB.id === mergeConflict.newData.id)
      ));
      
      setSelectedPair(null);
      setMergeConflict(null);
    } catch (error) {
      console.error('Error merging nodes:', error);
      toast.error(error.message || t('smartTools.duplicateDetector.mergeError'));
    } finally {
      setIsResolving(false);
    }
  };

  const handleIgnore = async (pair) => {
    try {
      await apiClient.post('/api/smart-tools/ignore-duplicate', {
        nodeAId: pair.nodeA.id,
        nodeBId: pair.nodeB.id
      });

      toast.info(t('smartTools.duplicateDetector.ignoreSuccess'));
      
      // Retirer la paire ignorée de la liste
      setDuplicates(prev => prev.filter(d => 
        !(d.nodeA.id === pair.nodeA.id && d.nodeB.id === pair.nodeB.id)
      ));
      
      setSelectedPair(null);
    } catch (error) {
      console.error('Error ignoring duplicate:', error);
      toast.error(t('smartTools.duplicateDetector.ignoreError'));
    }
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.95) return '#dc2626'; // Rouge - Très similaire
    if (similarity >= 0.90) return '#ea580c'; // Orange foncé
    if (similarity >= 0.85) return '#f59e0b'; // Orange
    return '#6b7280'; // Gris
  };

  const getSimilarityLabel = (similarity) => {
    if (similarity >= 0.95) return t('smartTools.duplicateDetector.veryHighSimilarity');
    if (similarity >= 0.90) return t('smartTools.duplicateDetector.highSimilarity');
    if (similarity >= 0.85) return t('smartTools.duplicateDetector.mediumSimilarity');
    return t('smartTools.duplicateDetector.lowSimilarity');
  };

  return (
    <div className="smart-duplicate-detector">
      <div className="detector-header">
        <div className="header-content">
          <h2>🔍 {t('smartTools.duplicateDetector.title')}</h2>
          <p className="header-description">{t('smartTools.duplicateDetector.description')}</p>
        </div>
        
        <div className="detector-controls">
          <div className="threshold-control">
            <label>{t('smartTools.duplicateDetector.threshold')}</label>
            <input
              type="range"
              min="0.7"
              max="0.99"
              step="0.01"
              value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              className="threshold-slider"
            />
            <span className="threshold-value">{(threshold * 100).toFixed(0)}%</span>
          </div>
          
          <button
            onClick={loadDuplicates}
            disabled={loading}
            className="refresh-button"
          >
            {loading ? '🔄' : '🔍'} {t('smartTools.duplicateDetector.scan')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('smartTools.duplicateDetector.scanning')}</p>
        </div>
      ) : duplicates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✨</div>
          <h3>{t('smartTools.duplicateDetector.noResults')}</h3>
          <p>{t('smartTools.duplicateDetector.noResultsDescription')}</p>
        </div>
      ) : (
        <div className="duplicates-list">
          <div className="list-header">
            <span>{t('smartTools.duplicateDetector.foundCount', { count: duplicates.length })}</span>
          </div>
          
          {duplicates.map((pair, index) => (
            <div key={`${pair.nodeA.id}-${pair.nodeB.id}`} className="duplicate-pair">
              <div className="pair-header">
                <div className="similarity-badge" style={{ backgroundColor: getSimilarityColor(pair.similarity) }}>
                  {(pair.similarity * 100).toFixed(1)}% {getSimilarityLabel(pair.similarity)}
                </div>
                <div className="pair-actions">
                  <button
                    onClick={() => setSelectedPair(selectedPair === pair ? null : pair)}
                    className="compare-button"
                  >
                    {selectedPair === pair ? '▼' : '▶'} {t('smartTools.duplicateDetector.compare')}
                  </button>
                </div>
              </div>

              <div className="pair-content">
                <div className="node-card">
                  <div className="node-header">
                    <span className="node-type">{pair.nodeA.type}</span>
                    <span className="node-badge">A</span>
                  </div>
                  <h4>{pair.nodeA.name}</h4>
                  <p className="node-description">{pair.nodeA.description || t('smartTools.duplicateDetector.noDescription')}</p>
                  <div className="node-meta">
                    <span>🏷️ {pair.nodeA.labels?.join(', ')}</span>
                  </div>
                </div>

                <div className="similarity-arrow">
                  ⟷
                </div>

                <div className="node-card">
                  <div className="node-header">
                    <span className="node-type">{pair.nodeB.type}</span>
                    <span className="node-badge">B</span>
                  </div>
                  <h4>{pair.nodeB.name}</h4>
                  <p className="node-description">{pair.nodeB.description || t('smartTools.duplicateDetector.noDescription')}</p>
                  <div className="node-meta">
                    <span>🏷️ {pair.nodeB.labels?.join(', ')}</span>
                  </div>
                </div>
              </div>

              <div className="reasons-section">
                <h5>{t('smartTools.duplicateDetector.reasons')}</h5>
                <ul className="reasons-list">
                  {pair.reasons.map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ul>
              </div>

              {selectedPair === pair && (
                <div className="comparison-panel">
                  <div className="comparison-grid">
                    <div className="comparison-column">
                      <h5>{t('smartTools.duplicateDetector.nodeA')} (#{pair.nodeA.id})</h5>
                      <div className="property-list">
                        {Object.entries(pair.nodeA).map(([key, value]) => (
                          key !== 'embedding' && key !== 'testsJson' && (
                            <div key={key} className="property-item">
                              <strong>{key}:</strong> {JSON.stringify(value)}
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                    
                    <div className="comparison-column">
                      <h5>{t('smartTools.duplicateDetector.nodeB')} (#{pair.nodeB.id})</h5>
                      <div className="property-list">
                        {Object.entries(pair.nodeB).map(([key, value]) => (
                          key !== 'embedding' && key !== 'testsJson' && (
                            <div key={key} className="property-item">
                              <strong>{key}:</strong> {JSON.stringify(value)}
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="action-buttons">
                    <button
                      onClick={() => handleMerge(pair)}
                      className="merge-button"
                    >
                      🔀 {t('smartTools.duplicateDetector.merge')}
                    </button>
                    <button
                      onClick={() => handleIgnore(pair)}
                      className="ignore-button"
                    >
                      ❌ {t('smartTools.duplicateDetector.ignore')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de merge interactive */}
      {mergeConflict && (
        <ConflictResolutionModal
          conflict={mergeConflict}
          onResolve={handleMergeResolve}
          onCancel={() => setMergeConflict(null)}
          isResolving={isResolving}
        />
      )}
    </div>
  );
};

export default SmartDuplicateDetector;
